import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

export const useUnreadMessages = (user: User | null) => {
  const [unreadMessages, setUnreadMessages] = useState(0);
  const fetchingRef = useRef(false);

  const fetchUnreadCount = useCallback(async () => {
    if (!user || fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const { data: convs } = await supabase
        .from("conversations")
        .select("id")
        .or(`participant_one.eq.${user.id},participant_two.eq.${user.id}`);

      if (!convs || convs.length === 0) {
        setUnreadMessages(0);
        return;
      }

      const convIds = convs.map((c) => c.id);
      const { count } = await supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .in("conversation_id", convIds)
        .neq("sender_id", user.id)
        .eq("read", false);
      setUnreadMessages(count ?? 0);
    } finally {
      fetchingRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    if (!user) return;

    const handleMessagesRead = () => fetchUnreadCount();

    const channel = supabase
      .channel("unread-msgs-" + user.id)
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, () => fetchUnreadCount())
      .subscribe();

    window.addEventListener("messages:read", handleMessagesRead);

    return () => {
      window.removeEventListener("messages:read", handleMessagesRead);
      supabase.removeChannel(channel);
    };
  }, [user, fetchUnreadCount]);

  return unreadMessages;
};
